import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Divider, Form, Input, Select, Typography } from 'antd';
import type { Workspace } from '@core/entities/Workspace';
import { MarkdownEditor } from '@presentation/components/workbench/MarkdownEditor';
import { WorkspaceDefaultSettingsSection } from '@presentation/components/workspace/WorkspaceDefaultSettingsSection';
import {
  useArchiveWorkspace,
  useDeleteWorkspace,
  useRestoreWorkspace,
  useTransferWorkspace,
  useUpdateWorkspace,
} from '@presentation/hooks/useWorkspaces';
import { useWorkspaceMembers } from '@presentation/hooks/useWorkspaceMembers';
import { useAppMessage } from '@presentation/hooks/useAppMessage';
import { ValidationError } from '@core/errors/AppError';
import { setAntdFormErrors } from '@shared/utils/formErrors';
import { paths } from '@app/routes/paths';

interface GeneralFormValues {
  name: string;
  description?: string;
}

export const GeneralTab = ({ workspace }: { workspace: Workspace }) => {
  const navigate = useNavigate();
  const updateWorkspace = useUpdateWorkspace(workspace.id);
  const archiveWorkspace = useArchiveWorkspace(workspace.id);
  const restoreWorkspace = useRestoreWorkspace(workspace.id);
  const transferWorkspace = useTransferWorkspace(workspace.id);
  const deleteWorkspace = useDeleteWorkspace();
  const { data: members } = useWorkspaceMembers(
    workspace.abilities.transfer ? workspace.id : undefined,
  );
  const [form] = Form.useForm<GeneralFormValues>();
  const message = useAppMessage();

  const [transferTarget, setTransferTarget] = useState<string | undefined>();
  const [confirmDelete, setConfirmDelete] = useState('');

  const handleSave = (values: GeneralFormValues) => {
    updateWorkspace.mutate(values, {
      onSuccess: () => message.success('Workspace updated.'),
      onError: (error) => {
        if (error instanceof ValidationError) {
          setAntdFormErrors(form, error.errors);
        }
      },
    });
  };

  const handleDelete = () => {
    deleteWorkspace.mutate(workspace.id, { onSuccess: () => navigate(paths.workspaces) });
  };

  const otherMembers = members?.filter((member) => !member.isOwner) ?? [];

  return (
    <>
      <div className="max-w-lg space-y-10">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: workspace.name, description: workspace.description ?? '' }}
          onFinish={handleSave}
          disabled={!workspace.abilities.update}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <MarkdownEditor
              placeholder="Document what this workspace is for, its conventions, and any onboarding notes…"
              minHeight={260}
            />
          </Form.Item>
          {workspace.abilities.update && (
            <Button type="primary" htmlType="submit" loading={updateWorkspace.isPending}>
              Save changes
            </Button>
          )}
        </Form>

        {workspace.abilities.update && (
          <>
            <Divider />
            <section className="space-y-3">
              <Typography.Title level={5}>Archive</Typography.Title>
              <Typography.Text type="secondary">
                Archiving hides this workspace from active use without deleting its data.
              </Typography.Text>
              <div>
                {workspace.isArchived ? (
                  <Button
                    onClick={() =>
                      restoreWorkspace.mutate(undefined, {
                        onSuccess: () => message.success('Workspace restored.'),
                      })
                    }
                    loading={restoreWorkspace.isPending}
                  >
                    Restore workspace
                  </Button>
                ) : (
                  <Button
                    onClick={() =>
                      archiveWorkspace.mutate(undefined, {
                        onSuccess: () => message.success('Workspace archived.'),
                      })
                    }
                    loading={archiveWorkspace.isPending}
                  >
                    Archive workspace
                  </Button>
                )}
              </div>
            </section>
          </>
        )}

        {workspace.abilities.transfer && otherMembers.length > 0 && (
          <>
            <Divider />
            <section className="space-y-3">
              <Typography.Title level={5}>Transfer ownership</Typography.Title>
              <Typography.Text type="secondary">
                Transfer this workspace to an existing member. You&apos;ll become an admin.
              </Typography.Text>
              <Select
                className="block"
                placeholder="Select a member…"
                value={transferTarget}
                onChange={setTransferTarget}
                options={otherMembers.map((member) => ({
                  value: member.user.id,
                  label: `${member.user.name} (${member.user.email})`,
                }))}
              />
              {transferWorkspace.isError && (
                <Alert type="error" showIcon title={transferWorkspace.error.message} />
              )}
              <div>
                <Button
                  loading={transferWorkspace.isPending}
                  disabled={!transferTarget}
                  onClick={() =>
                    transferTarget &&
                    transferWorkspace.mutate(transferTarget, {
                      onSuccess: () => message.success('Ownership transferred.'),
                    })
                  }
                >
                  Transfer ownership
                </Button>
              </div>
            </section>
          </>
        )}

        {workspace.abilities.delete && (
          <>
            <Divider />
            <Alert
              type="error"
              title="Danger zone"
              description={
                <div className="space-y-3">
                  <Typography.Text>
                    Type <Typography.Text code>{workspace.slug}</Typography.Text> to confirm
                    deletion. This cannot be undone.
                  </Typography.Text>
                  <Input
                    value={confirmDelete}
                    onChange={(event) => setConfirmDelete(event.target.value)}
                    placeholder={workspace.slug}
                  />
                  <Button
                    danger
                    disabled={confirmDelete !== workspace.slug}
                    loading={deleteWorkspace.isPending}
                    onClick={handleDelete}
                  >
                    Delete workspace
                  </Button>
                </div>
              }
            />
          </>
        )}
      </div>

      {workspace.abilities.update && (
        <div className="mt-10 max-w-3xl">
          <Divider />
          <Typography.Title level={5}>Default request settings</Typography.Title>
          <WorkspaceDefaultSettingsSection workspace={workspace} />
        </div>
      )}
    </>
  );
};
